import { Card, Metadata } from "@dropbox/dash-component-library";
import { Text } from "@dropbox/dig-components/typography";

import { Truncate } from "@dropbox/dig-components/truncate";
import { getTimeAgoString } from "@mirage/shared/util/time";
import styles from "./StackSourceCard.module.css";

import { DashSourceType } from "@mirage/analytics/events/enums/dash_source_type";
import { openURL } from "@mirage/service-platform-actions";
import { UrlSrcIcon } from "@mirage/shared/icons";
import { FileContentIcon } from "@mirage/shared/icons/FileContentIcon";
import i18n from "@mirage/translations";
import { useMemo } from "react";
import { StackConversationSource } from "./types";

type StackSourceCardProps = {
  onSourceClick: (sourceType: DashSourceType, documentUuid?: string) => void;
  source: StackConversationSource;
  sourceType: DashSourceType;
};

export function StackSourceCard({
  onSourceClick,
  source,
  sourceType,
}: StackSourceCardProps) {
  // We create the icon for the Stack Item here, because FileContentIcon makes certain
  // assumptions about SummarizableResult icons that do not apply to the icons shown
  // in the Stack source cards.
  const icon = useMemo(() => {
    return typeof source.icon === "string" ? (
      <UrlSrcIcon
        name={typeof source.title === "string" ? source.title : ""}
        iconUrl={source.icon}
        size={16}
      />
    ) : undefined;
  }, [source.icon, source.title]);

  const handleClick = () => {
    if (source.url) {
      // source.uuid is empty, rather than undefined, for unavailable sources
      // Passing undefined explicitly makes for more consistent logs
      onSourceClick(sourceType, source.uuid || undefined);
      openURL(source.url);
    }
  };

  return (
    <Card variant="outline" isLink className={styles.stackSourceCard}>
      <Card.Contents breakout="large">
        <Card.OverlayLink onClick={handleClick} href={source.url ?? "#"}>
          <Text size="medium" className={styles.stackSourceCardTitle}>
            <Truncate lines={2}>{source.title}</Truncate>
          </Text>
        </Card.OverlayLink>
      </Card.Contents>
      <Card.Contents breakout="large">
        <Metadata
          size="small"
          withDividers="blank"
          className={styles.stackSourceCardMetadata}
        >
          <Metadata.Item>
            <FileContentIcon
              content={source}
              hasBackground={false}
              icon={icon}
              size="small"
            />
          </Metadata.Item>
          <Metadata.Item>
            <Metadata.Label>
              {i18n.t("updated_ago", {
                timeAgo: getTimeAgoString(source.lastModifiedMs, {
                  abbreviateTime: true,
                  abbreviateDate: true,
                }),
              })}
            </Metadata.Label>
          </Metadata.Item>
        </Metadata>
      </Card.Contents>
    </Card>
  );
}
